import { useState, useEffect } from 'react';
import { ChevronDown, Award, MapPin, Calendar, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

export function CaseStudies() {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openStudy, setOpenStudy] = useState(null);

  useEffect(() => {
    loadSuccessStories();
  }, []);

  const loadSuccessStories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/success-stories`);
      const data = await response.json();
      setStories(data.filter(s => s.status === 'published'));
    } catch (err) {
      console.error('Failed to load success stories:', err);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="mb-12">
            <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Success Stories</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-4 leading-tight">
              Productions that trusted us with Mexico
            </h2>
          </div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-[hsl(var(--bk-line))] rounded-lg h-24 bg-[hsl(var(--bk-bg))] animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return null;
  }

  return (
    <section className="py-20 sm:py-28 bg-[hsl(var(--bk-bg-elev))]">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
        <div className="mb-12">
          <p className="font-mono-meta text-[hsl(var(--bk-accent))] mb-3">Success Stories</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[hsl(var(--bk-fg))] mb-4 leading-tight">
            Productions that trusted us with Mexico
          </h2>
          <p className="font-body text-lg text-[hsl(var(--bk-fg-muted))] max-w-[700px]">
            From global brands to award-winning documentaries — real projects, real results.
          </p>
        </div>

        <div className="space-y-4">
          {stories.map((story) => (
            <div 
              key={story.id}
              className="border border-[hsl(var(--bk-line))] rounded-lg overflow-hidden bg-[hsl(var(--bk-bg))]"
            >
              <button
                onClick={() => setOpenStudy(openStudy === story.id ? null : story.id)}
                className="w-full px-6 py-5 flex items-center gap-4 text-left hover:bg-[hsl(var(--bk-bg-elev))]/50 transition-colors"
                data-testid={`success-story-${story.slug}`}
              >
                {story.logo && (
                  <img src={story.logo} alt={story.client} className="h-8 object-contain grayscale opacity-60" />
                )}
                <div className="flex-1">
                  <h3 className="font-display text-xl text-[hsl(var(--bk-fg))] mb-1">{story.title}</h3>
                  <div className="flex flex-wrap items-center gap-4 font-body text-sm text-[hsl(var(--bk-fg-muted))]">
                    {story.location && <span className="flex items-center gap-1"><MapPin size={14} /> {story.location}</span>}
                    {story.year && <span className="flex items-center gap-1"><Calendar size={14} /> {story.year}</span>}
                  </div>
                </div>
                <ChevronDown
                  size={20}
                  className={`text-[hsl(var(--bk-fg-muted))] transition-transform flex-shrink-0 ${
                    openStudy === story.id ? 'rotate-180' : ''
                  }`}
                />
              </button>

              <AnimatePresence>
                {openStudy === story.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 pt-2 border-t border-[hsl(var(--bk-line))] space-y-6">
                      {story.challenge && (
                        <div>
                          <h4 className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-2">THE CHALLENGE</h4>
                          <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{story.challenge}</p>
                        </div>
                      )}

                      {story.solution && (
                        <div>
                          <h4 className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-2">OUR SOLUTION</h4>
                          <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">{story.solution}</p>
                        </div>
                      )}

                      {story.results && story.results.length > 0 && story.results[0] !== '' && (
                        <div>
                          <h4 className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] mb-3">RESULTS</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {story.results.map((result, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <Award size={16} className="text-[hsl(var(--bk-accent))] flex-shrink-0 mt-0.5" />
                                <span className="font-body text-sm text-[hsl(var(--bk-fg))]">{result}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {story.quote && (
                        <div className="p-5 rounded-lg bg-[hsl(var(--bk-accent))]/5 border border-[hsl(var(--bk-accent))]/20">
                          <p className="font-body text-sm text-[hsl(var(--bk-fg))] italic mb-2">"{story.quote}"</p>
                          {story.quote_attribution && (
                            <p className="font-body text-xs text-[hsl(var(--bk-fg-subtle))]">{story.quote_attribution}</p>
                          )}
                        </div>
                      )}

                      {story.project_link && (
                        <Link 
                          to={`/work/${story.project_link}`}
                          className="inline-flex items-center gap-2 font-body text-sm text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity"
                        >
                          View Project <ArrowRight size={16} />
                        </Link>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
